import type { ReactNode } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Skeleton,
  SkeletonItem,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { ArrowReset24Regular } from '@fluentui/react-icons';
import { FirstRunHint } from '../FirstRunHint';
import MokoCard from '../Layout/MokoCard';
import { themeVar } from '../theme/themeConfig';

export interface SummaryCardItem {
  key: string;
  label: string;
  value: string;
  icon: ReactNode;
  iconBg: string;
  iconFg: string;
  tone: string;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
}

const SELECTED_COLOR = themeVar(
  'tile',
  'selectedColor',
  tokens.colorBrandForeground1
);
const CARD_SHADOW = themeVar('card', 'shadow', '0 0 0 0 transparent');
const BORDER_COLOR = `var(--moko-summary-card-border-color, ${tokens.colorNeutralStroke1})`;
const ICON_SIZE = '40px';

const useStyles = makeStyles({
  wrapper: {
    marginBottom: tokens.spacingVerticalM,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    width: '100%',
    minWidth: 0,
  },
  headerTitle: {
    minWidth: 0,
  },
  reset: {
    flexShrink: 0,
  },
  accordionItem: {
    backgroundColor: 'transparent',
  },
  accordionHeader: {
    flex: 1,
    minWidth: 0,
    '& button': {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
      minHeight: 'unset',
      fontSize: '20px',
      lineHeight: '28px',
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  accordionPanel: {
    paddingTop: tokens.spacingVerticalS,
    marginLeft: 0,
    marginRight: 0,
    overflow: 'visible',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '14px',
    overflow: 'visible',
  },
  gridCell: {
    display: 'flex',
    maxWidth: '100%',
  },
  card: {
    boxSizing: 'border-box',
    width: 'max-content',
    maxWidth: '100%',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    padding: '16px 18px',
    font: 'inherit',
    textAlign: 'left',
    color: 'inherit',
    backgroundColor: 'transparent',
    border: `${tokens.strokeWidthThin} solid ${BORDER_COLOR}`,
    borderLeftWidth: '3px',
    borderRadius: 'var(--moko-summary-card-border-radius, 4px)',
    boxShadow: CARD_SHADOW,
    transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.15s',
  },
  selectable: {
    cursor: 'pointer',
  },
  hoverable: {
    ':hover': {
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      transform: 'translateY(-1px)',
    },
  },
  highlighted: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  selected: {
    borderTopColor: SELECTED_COLOR,
    borderRightColor: SELECTED_COLOR,
    borderBottomColor: SELECTED_COLOR,
    borderLeftColor: SELECTED_COLOR,
    boxShadow: `0 0 0 ${tokens.strokeWidthThin} ${SELECTED_COLOR}, ${CARD_SHADOW}`,
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '14px',
    minWidth: 0,
  },
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: ICON_SIZE,
    height: ICON_SIZE,
    borderRadius:
      'var(--moko-summary-card-icon-border-radius, var(--moko-summary-card-border-radius, 4px))',
    overflow: 'hidden',
    flexShrink: 0,
    '& svg, & i, & > span': {
      width: '20px',
      height: '20px',
      fontSize: '20px',
      display: 'inline-flex',
    },
  },
  textBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 0,
  },
  label: {
    color: themeVar('tile', 'labelColor', tokens.colorNeutralForeground1),
    fontSize: '12.5px',
    lineHeight: '16.25px',
    fontWeight: tokens.fontWeightMedium,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  value: {
    flexShrink: 0,
    color: themeVar('tile', 'valueColor', tokens.colorNeutralForeground1),
    fontSize: '24px',
    lineHeight: '26.4px',
    fontWeight: tokens.fontWeightBold,
    letterSpacing: '-0.02em',
  },
  skeletonText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXXS,
  },
});

interface SummaryCardsGridProps {
  title: string;
  items: SummaryCardItem[];
  loading?: boolean;
  skeletonCount?: number;
  defaultCollapsed?: boolean;
  resetLabel?: string;
  onReset?: () => void;
  hintId?: string;
  hintBody?: string;
  hintDismissLabel?: string;
}

const SummaryCardsGrid = ({
  title,
  items,
  loading,
  skeletonCount = 4,
  defaultCollapsed = false,
  resetLabel,
  onReset,
  hintId,
  hintBody,
  hintDismissLabel,
}: SummaryCardsGridProps) => {
  const styles = useStyles();
  const showReset = Boolean(onReset) && items.some((item) => item.selected);
  const firstSelectableKey = items.find((item) => item.selectable)?.key;

  const renderCard = (item: SummaryCardItem, highlighted: boolean) => {
    const selectable = Boolean(item.selectable);
    const selected = Boolean(item.selected);

    return (
      <div
        className={mergeClasses(
          styles.card,
          selectable && styles.selectable,
          selectable && !selected && styles.hoverable,
          highlighted && styles.highlighted,
          selected && styles.selected
        )}
        style={selected ? undefined : { borderLeftColor: item.tone }}
        role={selectable ? 'button' : undefined}
        tabIndex={selectable ? 0 : undefined}
        aria-pressed={selectable ? selected : undefined}
        onClick={selectable ? () => item.onSelect?.() : undefined}
        onKeyDown={
          selectable
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  item.onSelect?.();
                }
              }
            : undefined
        }
      >
        <div className={styles.cardBody}>
          <div
            className={styles.iconBox}
            style={{
              backgroundColor: item.iconBg,
              color: item.iconFg,
            }}
          >
            {item.icon}
          </div>
          <div className={styles.textBox}>
            <span className={styles.value}>{item.value}</span>
            <Text className={styles.label}>{item.label}</Text>
          </div>
        </div>
      </div>
    );
  };

  const renderGrid = () => {
    if (loading) {
      return (
        <div className={styles.grid}>
          {Array.from({ length: skeletonCount }, (_, i) => (
            <div key={i} className={styles.card}>
              <Skeleton>
                <div className={styles.cardBody}>
                  <SkeletonItem
                    shape="square"
                    style={{ width: ICON_SIZE, height: ICON_SIZE }}
                  />
                  <div className={styles.skeletonText}>
                    <SkeletonItem style={{ width: 28 }} size={16} />
                    <SkeletonItem style={{ width: 72 }} size={16} />
                  </div>
                </div>
              </Skeleton>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className={styles.grid}>
        {items.map((item) => {
          const card = (highlighted: boolean) => renderCard(item, highlighted);
          if (
            hintId &&
            hintBody &&
            item.selectable &&
            item.key === firstSelectableKey
          ) {
            return (
              <div key={item.key} className={styles.gridCell}>
                <FirstRunHint
                  id={hintId}
                  body={hintBody}
                  dismissLabel={hintDismissLabel}
                >
                  {card}
                </FirstRunHint>
              </div>
            );
          }
          return (
            <div key={item.key} className={styles.gridCell}>
              {card(false)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <MokoCard className={styles.wrapper}>
      <Accordion
        collapsible
        defaultOpenItems={defaultCollapsed ? [] : ['summary']}
      >
        <AccordionItem value="summary" className={styles.accordionItem}>
          <div className={styles.headerRow}>
            <AccordionHeader
              className={styles.accordionHeader}
              expandIconPosition="end"
            >
              <span className={styles.headerTitle}>{title}</span>
            </AccordionHeader>
            {showReset && (
              <Button
                className={styles.reset}
                appearance="outline"
                size="small"
                icon={<ArrowReset24Regular />}
                aria-label={resetLabel}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onReset?.();
                }}
              >
                {resetLabel}
              </Button>
            )}
          </div>
          <AccordionPanel className={styles.accordionPanel}>
            {renderGrid()}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </MokoCard>
  );
};

export default SummaryCardsGrid;
